/**
 * Playbook Live card frames -> the SAME Redux actions a live sales call dispatches.
 *
 * Every Playbook Live card arrives in the shapes production_final_server and
 * the detection services send: objection/FAQ questions, New Inquiries,
 * landmine and competitor alerts. Each concern on the track gets exactly one
 * card, which the services build from the track when the customer raises it
 * (owner, 2026-10-06; it adds `trackItemId`, which nothing here reads); every
 * other line is carded by live detection, as on a call. They land in
 * conversationSlice (the feed, and the New Inquiries bar, which reads
 * `category === 'New Inquiry'` from the same entries), landmineSlice and
 * competitorSlice. Mirrors websocketService (questions) and ConversationPage's
 * standalone alert handling (alerts).
 *
 * Knowledge-base training does NOT use this: its cards are built by the
 * server per item (see trainingCallActions).
 *
 * Pure on purpose: `now` is passed in, so the mapping is pinned by tests.
 * Every frame is untrusted socket JSON and is read defensively.
 *
 * Answers pair with their card by question text here, not by card key
 * (utils/cardAnswerPairing). Since 2026-10-03 the services stamp Playbook
 * Live's answer frames with the card's own `timestamp`, `sourceId` and
 * `parentId` (`answer_frame` in the services' playbook_live), so these frames
 * could now be keyed as a live call's are; that is not done yet.
 */
import type { AnyAction } from '@reduxjs/toolkit';

import { addAnswer, addQuestion, addStoredAnswer, markAnswerError } from '../store/conversationSlice';
import { addCompetitorAlert } from '../store/slices/competitorSlice';
import { addLandmineAlert } from '../store/slices/landmineSlice';
import { ConversationCategory } from '../types/conversation';
import { toText } from '../services/kbLiveFrames';

export type LiveDetectionFrame =
  | 'question_detected'
  | 'stored_answer_generated'
  | 'answer_generated'
  | 'answer_error'
  | 'landmine_alert'
  | 'competitor_alert';

type WireRecord = Record<string, unknown>;
type LandminePayload = Parameters<typeof addLandmineAlert>[0];
type CompetitorPayload = Parameters<typeof addCompetitorAlert>[0];

/** The live-call spellings of "no question here", filtered like websocketService does. */
const NOT_A_QUESTION = new Set(['<Question Not Detected>', 'Question Not Detected']);
/** Detection speakers are the training transcript's; alert labels read the live-call ones. */
const ALERT_SPEAKER: Record<string, string> = { ai_customer: 'customer', salesperson: 'salesperson' };
const DEFAULT_LANDMINE_TYPE = 'legal_risk';
const DEFAULT_PRIORITY = 'medium';
const LANDMINE_TYPES = ['legal_risk', 'detractor'] as const;
const PRIORITIES = ['high', 'medium', 'low'] as const;

const asRecord = (value: unknown): WireRecord =>
  value && typeof value === 'object' ? (value as WireRecord) : {};

const toNumber = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

const toList = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const oneOf = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  allowed.find((candidate) => candidate === value) ?? fallback;

const stampOf = (record: WireRecord, now: string): string => toText(record?.timestamp) || now;

function questionActions(record: WireRecord, now: string): AnyAction[] {
  const question = toText(record?.question).trim();
  const category = toText(record?.category);
  const relevanceScore = toNumber(record?.relevance_score);
  const isLegitimate =
    question !== '' &&
    !NOT_A_QUESTION.has(question) &&
    (relevanceScore > 0 || category === ConversationCategory.NEW_INQUIRY);
  if (!isLegitimate) return [];
  return [addQuestion({ question, category, relevance_score: relevanceScore, timestamp: stampOf(record, now) })];
}

function landmineActions(record: WireRecord, now: string): AnyAction[] {
  const trigger = toText(record?.trigger);
  const matchedText = toText(record?.matched_text);
  if (!trigger || !matchedText) return [];
  const detectedAt = toText(record?.detected_at) || now;
  const alert: LandminePayload = {
    trigger,
    matched_text: matchedText,
    matched_phrase: toText(record?.matched_phrase),
    similarity_score: toNumber(record?.similarity_score),
    semantic_match: record?.semantic_match === true,
    landmine_type: oneOf(record?.landmine_type, LANDMINE_TYPES, DEFAULT_LANDMINE_TYPE),
    category: toText(record?.category),
    priority: oneOf(record?.priority, PRIORITIES, DEFAULT_PRIORITY),
    can_say: toList(record?.can_say),
    cannot_say: toList(record?.cannot_say),
    quick_tips: toText(record?.quick_tips),
    detected_at: detectedAt,
    timestamp: detectedAt,
    speaker: ALERT_SPEAKER[toText(record?.speaker)] ?? toText(record?.speaker),
  };
  return [addLandmineAlert(alert)];
}

function competitorActions(record: WireRecord, now: string): AnyAction[] {
  const competitor = toText(record?.competitor);
  const matchedText = toText(record?.matched_text);
  if (!competitor || !matchedText) return [];
  const detectedAt = toText(record?.detected_at) || now;
  const alert: CompetitorPayload = {
    competitor,
    matched_text: matchedText,
    confidence: toNumber(record?.confidence),
    match_type: toText(record?.match_type),
    advantages: toList(record?.advantages),
    weaknesses: toList(record?.weaknesses),
    talking_points: toText(record?.talking_points),
    category: toText(record?.category),
    detected_at: detectedAt,
    timestamp: detectedAt,
    speaker: ALERT_SPEAKER[toText(record?.speaker)] ?? toText(record?.speaker),
  };
  return [addCompetitorAlert(alert)];
}

/** The actions one live detection frame dispatches; [] for a frame to drop. */
export function liveDetectionActions(frame: LiveDetectionFrame, raw: unknown, now: string): AnyAction[] {
  const record = asRecord(raw);
  const question = toText(record?.question);
  switch (frame) {
    case 'question_detected':
      return questionActions(record, now);
    case 'stored_answer_generated':
      return [addStoredAnswer({ question, stored_answer: toText(record?.stored_answer), timestamp: stampOf(record, now) })];
    case 'answer_generated':
      return [addAnswer({ question, answer: toText(record?.answer), timestamp: stampOf(record, now) })];
    case 'answer_error':
      return [markAnswerError({ question, error: toText(record?.error), timestamp: stampOf(record, now) })];
    case 'landmine_alert':
      return landmineActions(record, now);
    case 'competitor_alert':
      return competitorActions(record, now);
    default:
      return [];
  }
}
